import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ContractStatus } from 'components/ContractStatus';
import type { ITabItem } from 'components/Form/ControlledTab';
import { ReadField } from 'components/ReadField';
import { TooltipWrapper } from 'components/TooltipWrapper';
import { Viewport } from 'components/Viewport';
import { RequestStatus } from 'features/request/requestSlice';
import type { ContractResultsViewData, UerContractData } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useRunAfterSuccessRequest, useRunWithChangeCheck } from 'hooks/useRunWithChangeCheck';
import { useRemainderPie } from 'pages/Project/Common/hooks/useRemainderPie';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { TabsIds } from 'pages/Project/Common/types';
import { contractTabs } from 'pages/Project/Common/utils';
import { PriceOptions } from 'pages/Project/UerProject/PriceOptions/PriceOptions';
import { Grid, GridItem, Text, CollapseGroup, cnMixSpace, Tabs, Pie, IconQuestion, Collapse } from 'uikit';
import { formatNumberToRu } from 'utils';

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ContractAccumulationSheetGrid } from '../ContractAccumulationSheetGrid/ContractAccumulationSheetGrid';
import { useContractResultsData } from '../hooks/useContractData';
import type { ParsedContractData } from '../utils';

import Style from './ContractReadForm.module.css';

interface ContractUpdateFormProps {
    defaultValues: ParsedContractData;
    makeContractDataRequest: any;
    data: UerContractData;
}

const PieStyle = { width: 250, height: 170 };

export const roundingTooltip =
    'Выбранная разрядность будет применена к расчету и отображению стоимости на форме Наряд-Заказа и Изменения к Наряд-Заказу в рамках текущего проекта';

export const ContractReadForm = ({ defaultValues, makeContractDataRequest, data }: ContractUpdateFormProps) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState<ITabItem<TabsIds>>(contractTabs[0]);
    const [isOpen, setOpen] = useState<boolean>(true);
    const [gotAccumulationData, setGotAccumulationData] = useState(false);
    const { projectData } = useProjectData(id, false);
    const hasWorkOrders = useMemo(
        () => !!projectData?.contractNavigation?.data?.workOrderNavs?.length,
        [projectData?.contractNavigation?.data?.workOrderNavs?.length],
    );
    const hasWorkOrdersInProgressOrCompleted = useMemo(
        () =>
            !!projectData?.contractNavigation?.data?.workOrderNavs?.filter((w) =>
                [WorkOrderStatusEnum.InProgress, WorkOrderStatusEnum.Completed].includes(w.status),
            )?.length,
        [projectData?.contractNavigation?.data?.workOrderNavs],
    );
    const isAccumulationSheetActive = activeTab?.id === TabsIds.ACCUMULATION_SHEET;
    const [
        contractResultsData,
        getContractResultsStatus,
        ,
        ,
        getContractResultsSetData,
        getContractResultsRefreshData,
    ] = useContractResultsData(defaultValues?.id, isAccumulationSheetActive && !gotAccumulationData && hasWorkOrders);

    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);

    useRunWithChangeCheck({
        initialData: contractResultsData?.workOrders,
        uniqueIdentifier: 'id',
        compareKeys: ['status', 'internalNumber', 'signingDate'],
        compareDataRequestId: 'updateWorkOrder',
        action: getContractResultsRefreshData,
        withEffect: Boolean(contractResultsData?.workOrders),
    });

    useRunAfterSuccessRequest({
        compareDataRequestId: 'deleteWorkOrder',
        action: getContractResultsRefreshData,
        withEffect: Boolean(contractResultsData?.workOrders),
        additionalCompareKeys: ['id', 'validation'],
    });

    const isLoading = getContractResultsStatus === RequestStatus.FETCHING;

    useEffect(
        () => () => {
            dispatch(getContractResultsSetData(undefined));
        },
        [dispatch, getContractResultsSetData],
    );

    useEffect(() => {
        if (contractResultsData && getContractResultsStatus === RequestStatus.SUCCESS) {
            setGotAccumulationData(true);
        }
    }, [contractResultsData, getContractResultsStatus]);

    const contractResultData: ContractResultsViewData = useMemo(
        () =>
            hasWorkOrders
                ? contractResultsData
                : { contractResults: defaultValues?.contractPlans || [], workOrders: [] },
        [defaultValues?.contractPlans, contractResultsData, hasWorkOrders],
    );

    const handleTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const costRounding = projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0;

    const commonGroups = useMemo(
        () => [
            {
                label: 'Данные договора',
                content: (
                    <>
                        <ReadField value={defaultValues.subject} label="Предмет" />
                        <ReadField value={defaultValues.internalNumber} label="Внутренний номер" />
                        <ReadField value={defaultValues.customer} label="Заказчик" />
                        <ReadField value={defaultValues.customerAddress} label="Адрес заказчика" />
                        <ReadField value={defaultValues.contractor} label="Подрядчик" />
                        <ReadField value={defaultValues.contractorAddress} label="Адрес подрядчика" />
                    </>
                ),
            },
            {
                label: 'Даты договора',
                content: (
                    <>
                        <ReadField
                            value={defaultValues.signingDate}
                            label="Подписание"
                            render={(value) => <Text size="s">{value?.toLocaleDateString()}</Text>}
                        />
                        <ReadField
                            value={defaultValues.startDate}
                            label="Начало действия"
                            render={(value) => <Text size="s">{value?.toLocaleDateString()}</Text>}
                        />
                        <ReadField
                            value={defaultValues.finishDate}
                            label="Окончание действия"
                            render={(value) => <Text size="s">{value?.toLocaleDateString()}</Text>}
                        />
                    </>
                ),
            },
            {
                label: 'Сумма договора',
                content: (
                    <>
                        <ReadField
                            value={defaultValues.cost.contractPlanWithTax}
                            label="Сумма с НДС (руб.)"
                            render={(value) =>
                                value !== undefined && <Text size="s">{formatNumberToRu(Number(value), 2)}</Text>
                            }
                        />
                        <ReadField
                            value={defaultValues.cost.contractPlan}
                            label="Сумма без НДС (руб.)"
                            render={(value) => <Text size="s">{formatNumberToRu(value, costRounding)}</Text>}
                        />
                        <ReadField
                            value={defaultValues.cost.contractPlanTax}
                            label="НДС, 20% (руб.)"
                            render={(value) => <Text size="s">{formatNumberToRu(value, 2)}</Text>}
                        />
                    </>
                ),
            },
            {
                label: 'Коды организации',
                content: (
                    <>
                        <ReadField value={defaultValues.codeOKUD} label="Форма по ОКУД" />
                        <ReadField value={defaultValues.customerOKPO} label="По ОКПО Заказчик" />
                        <ReadField value={defaultValues.contractorOKPO} label="По ОКПО Подрядчик" />
                        <ReadField value={defaultValues.codeOKPD} label="Вид деятельности по ОКДП" />
                    </>
                ),
            },
            {
                label: 'Дополнительно',
                content: <ReadField value={defaultValues.note} label="Примечание" />,
            },
        ],
        [costRounding, defaultValues],
    );

    const commonGroupsOpened = useMemo(() => commonGroups.map((_, index) => index), [commonGroups]);

    const { pieData, pieColor, pieStatistic, pieTooltip } = useRemainderPie(data.cost);

    const topIndention = useMemo(() => {
        if (!isHeaderOpened && !isOpen) {
            return '-210px';
        }
        if (isHeaderOpened && isOpen) {
            return '160px';
        }
        if (isHeaderOpened && !isOpen) {
            return '-50px';
        }
        if (!isHeaderOpened && isOpen) {
            return '-5px';
        }

        return '';
    }, [isHeaderOpened, isOpen]);

    return (
        <>
            <div className={cnMixSpace({ mB: 's' })}>
                <Collapse label="Основная информация" isOpen={isOpen} onClick={() => setOpen(!isOpen)} size="m">
                    <div style={{ display: 'flex', overflow: 'hidden' }}>
                        <div className={hasWorkOrders ? Style.wrapperContent : ''}>
                            <div className={Style.wrapReadField}>
                                <ReadField
                                    value={defaultValues.status}
                                    label="Статус"
                                    render={(value) => <ContractStatus id={value.id} />}
                                />
                                <ReadField value={defaultValues.authoredBy} label="Автор" />
                                <ReadField
                                    value={defaultValues.costRounding}
                                    label="Округление стоимости"
                                    render={(value) => <Text size="s">{value?.label}</Text>}
                                    labelRightSide={
                                        <ComponentWithTooltip
                                            tooltipText={roundingTooltip}
                                            styles={{ height: 16 }}
                                            direction="rightCenter"
                                        >
                                            <IconQuestion view="secondary" size="s" />
                                        </ComponentWithTooltip>
                                    }
                                />
                                <ReadField value={defaultValues.catalogUerBase?.name} label="Справочник расценок" />
                            </div>
                            {hasWorkOrders && (
                                <div className={Style.tooltipContent}>
                                    <div className={Style.wrapperPie}>
                                        <TooltipWrapper
                                            direction="downCenter"
                                            message={pieTooltip}
                                            isHidden={!pieTooltip}
                                            toolTipClassName={Style.wrapperPieTooltip}
                                        >
                                            <Pie
                                                angleField="value"
                                                colorField="type"
                                                data={pieData}
                                                color={pieColor}
                                                innerRadius={0.75}
                                                label={undefined}
                                                legend={false}
                                                tooltip={false}
                                                animation={false}
                                                statistic={pieStatistic}
                                                style={PieStyle}
                                            />
                                        </TooltipWrapper>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Collapse>
            </div>
            <Grid rowGap="xl" cols="1" style={{ height: 36 }}>
                <GridItem>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        items={contractTabs}
                        getItemLabel={(item) => item.label}
                        view="bordered"
                        size="s"
                    />
                </GridItem>
            </Grid>
            <Viewport
                topIndention={topIndention}
                marginBottom="var(--space-margin-bottom)"
                shownChildIndex={activeTab.id}
            >
                <div style={{ maxWidth: '520px' }}>
                    <CollapseGroup
                        items={commonGroups}
                        opened={commonGroupsOpened}
                        style={{ margin: 'var(--space-margin-bottom)', marginLeft: 0 }}
                    />
                </div>
                <PriceOptions defaultValues={defaultValues} makeContractDataRequest={makeContractDataRequest} />
                <ContractAccumulationSheetGrid
                    contract={defaultValues}
                    isLoading={isLoading}
                    data={contractResultData}
                    hasWorkOrders={hasWorkOrders}
                    hasWorkOrdersInProgressOrCompleted={hasWorkOrdersInProgressOrCompleted}
                    costRounding={data.costRounding}
                />
            </Viewport>
        </>
    );
};

ContractReadForm.displayName = 'ContractReadForm';
